import { Dialog, DialogActions, DialogContent, Rating, TextField, Typography } from '@mui/material';
import { MprcContainer, MprcDialogTitleClose, MprcRequestIndicator } from 'components';
import { ExtractFieldErrors, MprcRequestErrorReport } from 'components/MprcRequestErrorReport';
import { ButtonCancel, ButtonConfirm } from 'components/buttons';
import { EnrollmentRatingResponse } from 'generated/CoreApi_v1';
import LibUtils from 'lib/LibUtils';
import { enqueueSnackbar } from 'notistack';
import { useCoreApi } from 'providers/CoreApiProvider';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface IProps {
    enrollmentId: string;
    trainingName: string;
    onClose: (success?: boolean) => void;
}

export const TrainingEditRating: React.FC<IProps> = (props) => {

    const { enrollmentId, trainingName, onClose } = props;
    const { t } = useTranslation("lms");
    const api = useCoreApi();

    const [loading, setLoading] = useState<boolean>(true);
    const [saving, setSaving] = useState<boolean>(false);
    const [viewData, setViewData] = useState<EnrollmentRatingResponse>();
    const [viewError, setViewError] = useState<any>();
    const [loadDto, setLoadDto] = useState<EnrollmentRatingResponse>();
    const [recordChanged, setRecordChanged] = useState<boolean>(false);

    const canSave = !loading && (viewData?.ratingStars || 0) > 0 && recordChanged;

    ///
    const updateRecord = (key: keyof EnrollmentRatingResponse, v: any) => {
        setViewData(state => ({ ...state!, [key]: v }));
    };

    ///
    const handleSave = () => {
        setSaving(true);
        api.v1.enrollment.addRating({
            enrollmentId,
            ratingComment: viewData?.ratingComment ?? null,
            ratingStars: viewData?.ratingStars ?? 0
        })
            .then(response => {
                if (response.data === true) {
                    enqueueSnackbar(t("Student.TrainingRatingView.SaveSuccessMessage"), { variant: "success" });
                    onClose(true);
                }
                else {
                    enqueueSnackbar(t("Student.TrainingRatingView.SaveErrorMessage"), { variant: "error" });
                }
            })
            .catch(e => {
                var errors = ExtractFieldErrors(e);
                errors.forEach(m => enqueueSnackbar(m, { variant: "error" }));
            })
            .finally(() => setSaving(false));
    };

    useEffect(() => {
        async function getData() {
            try {
                const response = await api.v1.enrollment.getEnrollmentRating({ EnrollmentId: enrollmentId });
                setViewData(response.data);
                setLoadDto(LibUtils.deepCopy(response.data));
            }
            catch (e) {
                setViewError(e);
            }
            finally {
                setLoading(false);
            }
        }
        getData();
    }, []);

    useEffect(() => {
        const isRecordChanged = !LibUtils.deepCompare(viewData, loadDto);
        setRecordChanged(isRecordChanged);
    }, [viewData]);

    return (
        <>
            <MprcRequestIndicator visible={loading || saving} type={loading ? "request" : "post"} />
            <Dialog
                open
                fullWidth
                maxWidth="sm"
                onClose={() => onClose()}
            >
                <MprcDialogTitleClose
                    shrinkPadding
                    title={t("Student.TrainingRatingView.Title")}
                    subtitle={trainingName}
                    onClose={() => onClose()}
                />

                <DialogContent>
                    <MprcRequestErrorReport error={viewError} />
                    <MprcContainer>
                        <Typography color="muted.main">{t("Student.TrainingRatingView.StarsLabel")} *</Typography>
                        <Rating
                            size="large"
                            value={viewData?.ratingStars || 0}
                            onChange={(e, value) => updateRecord("ratingStars", value)}
                        />
                    </MprcContainer>
                    <MprcContainer>

                        <TextField
                            fullWidth
                            value={viewData?.ratingComment || ""}
                            label={t("Student.TrainingRatingView.CommentLabel")}
                            onChange={e => updateRecord("ratingComment", e.target.value)}
                            multiline
                            minRows={3}
                            autoComplete="off"
                            slotProps={{
                                input: {
                                    inputProps: { maxLength: 2048 }
                                }
                            }}
                        />
                    </MprcContainer>
                </DialogContent>
                <DialogActions>
                    <ButtonCancel disabled={loading} variant="contained" onClick={() => onClose()} />
                    <ButtonConfirm
                        disabled={!canSave}
                        text={t("Student.TrainingRatingView.SaveButtonText")}
                        variant="contained"
                        onClick={handleSave}
                    />
                </DialogActions>
            </Dialog>
        </>
    );
};
